import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Plus, Search, Calendar, MapPin, Sparkles } from "lucide-react";
import { Header } from "@/components/Layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { EventStatusBadge } from "@/components/Events/EventStatusBadge";
import { EventForm } from "@/components/Events/EventForm";
import { AIAssistantDialog } from "@/components/AI/AIAssistantDialog";
import { useEvents } from "@/hooks/useEvents";

export function EventsPage() {
  const { data: events = [], isLoading } = useEvents();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  const navigate = useNavigate();

  const isFormOpen = searchParams.get("new") === "true";

  const filteredEvents = events.filter(
    (e) =>
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.location.toLowerCase().includes(search.toLowerCase()),
  );

  const handleCloseForm = () => {
    setSearchParams({});
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Header title="Événements" />

      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher un événement..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <Button
            onClick={() => setIsAIAssistantOpen(true)}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Assistant IA
          </Button>

          <Button
            onClick={() => setSearchParams({ new: "true" })}
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouvel événement
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredEvents.map((event) => (
            <Link key={event.id} to={`/events/${event.id}`}>
              <Card className="transition-shadow hover:shadow-md">
                <CardContent className="p-4">
                  <div className="mb-3 flex items-start justify-between">
                    <h3 className="font-semibold">{event.name}</h3>
                    <EventStatusBadge status={event.status} />
                  </div>

                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {format(new Date(event.event_date), "d MMMM yyyy", {
                          locale: fr,
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span className="line-clamp-1">{event.location}</span>
                    </div>
                    {event.organizer && (
                      <p className="text-xs">{event.organizer.name}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {filteredEvents.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">
            Aucun événement trouvé
          </div>
        )}
      </div>

      <EventForm open={isFormOpen} onClose={handleCloseForm} />

      <AIAssistantDialog
        open={isAIAssistantOpen}
        onClose={() => setIsAIAssistantOpen(false)}
        onEventCreated={(eventId) => navigate(`/events/${eventId}`)}
      />
    </div>
  );
}
